import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectIsLoading, selectMeal } from 'store/nutrition/selectors';

import { countDayIndex } from 'helpers/dateParser';

import { trackMealPageLoad } from '@/analytics/trackers/nutrition';

type UseGetMealDataReturnType = {
    meal: {
        mealType?: string;
        cover?: string;
        header: {
            name?: string;
            difficulty?: string;
            cookingTime?: number;
            copy: { description: string };
        };
        macros: {
            calories?: number;
            carbs?: number;
            protein?: number;
            fat?: number;
            copy: {
                disclaimer: string;
                fat: string;
                carbs: string;
                protein: string;
                calories: string;
                measurement: string;
            };
        };
        ingredients: {
            copy: { title: string };
            ingredients?: {
                name: string;
                measure: string;
                quantity: number | string;
            }[];
        };
        instructions: {
            copy: { title: string };
            instructions?: {
                position: number;
                text: string;
            }[];
        };
    };
    copy: {
        errorTitle: string;
        errorDescription: string;
        button: string;
    };
    state: 'loading' | 'success' | 'error';
    trackMealPageLoad: () => void;
};

export const useGetMeal = (selectedDate: Date = new Date()): UseGetMealDataReturnType => {
    const meal = useSelector(selectMeal);
    const { t } = useTranslation();
    const isLoading = useSelector(selectIsLoading);

    const header = {
        name: meal?.name,
        difficulty: meal?.cookingLevel,
        cookingTime: meal?.cookingTime,
        copy: { description: ' min • ' },
    };

    const macros = {
        calories: meal?.nutrients.kilocalories,
        carbs: meal?.nutrients.carbs,
        protein: meal?.nutrients.protein,
        fat: meal?.nutrients.fat,
        copy: {
            disclaimer: t('mealPage.disclaimer'),
            fat: t('mealPage.fat'),
            carbs: t('mealPage.carbs'),
            protein: t('mealPage.protein'),
            calories: t('mealPage.calories'),
            measurement: 'g',
        },
    };

    const ingredients = {
        copy: { title: t('mealPage.ingredients') },
        ingredients: meal?.ingredients,
    };

    const instructions = {
        copy: { title: t('mealPage.instructions') },
        instructions: meal?.steps,
    };

    const getMealState = (): 'loading' | 'success' | 'error' => {
        if (isLoading) {
            return 'loading';
        }
        if (!meal && !isLoading) {
            return 'error';
        }

        return 'success';
    };

    const handleTrackMealPageLoad = () => {
        trackMealPageLoad(countDayIndex(selectedDate), getMealState());
    };

    return {
        meal: {
            mealType: meal?.mealType,
            cover: meal?.cover,
            header,
            macros,
            ingredients,
            instructions,
        },
        copy: {
            errorTitle: t('mealPage.errorTitle'),
            errorDescription: t('mealPage.errorDescription'),
            button: t('mealPage.button'),
        },
        state: getMealState(),
        trackMealPageLoad: handleTrackMealPageLoad,
    };
};
